import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive } from "vue";
  import { useRoute } from "vue-router";
  import projectPageApi, {
    ProjectPageData,
    ResidentialContent,
  } from "@/services/api/projectPageApi";
  import LandSubdivisionPublicProjectPage from "@/views/project-page/LandSubdivisionPublicProjectPage.vue";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'PublicProjectPage',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const slug = computed(() => route.params.slug as string);
  const shortCode = computed(() => route.params.shortCode as string);

  const pageData = reactive<ProjectPageData>({});
  const loading = reactive({ value: false });
  const error = reactive({ value: null as string | null });

  async function fetchPageData() {
    if (!slug.value && !shortCode.value) return;

    try {
      loading.value = true;
      error.value = null;
      const { data } = shortCode.value
        ? await projectPageApi.getByShortCode(shortCode.value)
        : await projectPageApi.getBySlug(slug.value);

      Object.assign(pageData, data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };

      error.value =
        err?.response?.data?.message || "Failed to load project page";
      console.error("Failed to fetch project page:", e);
    } finally {
      loading.value = false;
    }
  }

  onMounted(fetchPageData);

  const isLandSubdivision = computed(() => Boolean(pageData.landSubContent));

  const heroBgStyle = computed(() => {
    if (pageData.backgroundImageUrl) {
      return { backgroundImage: `url(${pageData.backgroundImageUrl})` };
    }

    return { backgroundColor: "#2d3a3a" };
  });

  const MOCKUP_CONTENT: ResidentialContent = {
    brandText: "Your Brand",
    headline: "Your headline here",
    description: "Add a description for your project.",
    actionButtons: [
      { label: "Browse Home Designs", destination: "home" },
      { label: "Explore Map", destination: "map" },
      { label: "View Documents", destination: "document" },
    ],
  };

  // Older project-page records can exist with an empty residential payload.
  // Render the placeholders publicly without changing the landowner editor or
  // persisting data that the landowner has not chosen to save.
  const displayContent = computed<ResidentialContent>(() => {
    const content = pageData.residentialContent;
    const hasCustomContent = Boolean(
      content?.logoImageUrl ||
        content?.brandText?.trim() ||
        content?.headline?.trim() ||
        content?.description?.trim() ||
        content?.actionButtons?.some((button) => button.label?.trim())
    );

    return hasCustomContent ? content ?? {} : MOCKUP_CONTENT;
  });

  const visibleButtons = computed(() =>
    (displayContent.value.actionButtons ?? []).filter((button) => button.label)
  );

  const BUTTON_ICONS: Record<string, string> = {
    home: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
    map: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>`,
    document: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`,
  };

  function getButtonIcon(destination: string) {
    return BUTTON_ICONS[destination] ?? BUTTON_ICONS.document;
  }

  function getButtonHref(destination: string): string {
    const estateId = pageData.estateId;
    const embedSuffix = route.query.embed === "true" ? "?embed=true" : "";

    if (destination === "home" && estateId) {
      return `/v2/estate/${estateId}/home-designs${embedSuffix}`;
    }

    if (destination === "map" && estateId) {
      return `/v2/estate/masterplan/${estateId}${embedSuffix}`;
    }

    return "#";
  }

const __returned__ = { route, slug, shortCode, pageData, loading, error, fetchPageData, isLandSubdivision, heroBgStyle, MOCKUP_CONTENT, displayContent, visibleButtons, BUTTON_ICONS, getButtonIcon, getButtonHref, computed, onMounted, reactive, get useRoute() { return useRoute }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, get ResidentialContent() { return ResidentialContent }, LandSubdivisionPublicProjectPage }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})